import {
  GITHUB_URL_OR_EMPTY_REGEX,
  SO_URL_OR_EMPTY_REGEX,
  TWITTER_URL_OR_EMPTY_REGEX,
  URL_OR_EMPTY_REGEX
} from '@asgard/utils';
import { TFunction } from 'hooks/useI18n';
import { Experience, MANFREDITE_LEVEL } from 'interfaces/manfredite.interface';
import { Tech } from 'interfaces/offer.interface';
import { ListItem } from 'interfaces/ui/ui-list-item';
import { Media } from 'model';
import { isPossiblePhoneNumber } from 'react-phone-number-input';
import {
  AllowedManfrediteFileType,
  deleteFile,
  uploadManfrediteFile
} from 'services/manfredite-file.service';
import { asyncForEach } from 'utils/async';
import { getTranslation } from 'utils/i18n';
import {
  yupMultilingual,
  yupOptionalListItem,
  yupOptionalListItemArray
} from 'utils/yup';
import * as yup from 'yup';

export function getExperienceLabel(
  { min, max }: Experience,
  locale?: string
): string {
  if (min && !max) {
    return getTranslation('experience.min', 'common', locale, { min });
  }

  if (!min && max) {
    return getTranslation('experience.max', 'common', locale, { max });
  }

  if (min === 0 && max === 0) {
    return getTranslation('experience.equal', 'common', locale);
  }

  return getTranslation('experience.minMax', 'common', locale, { min, max });
}

export async function getAttachmentsIds(
  attachments: {
    type: AllowedManfrediteFileType;
    currentId?: number;
    formValue?: File[];
    media?: Media;
    setMediaState: React.Dispatch<React.SetStateAction<Media | undefined>>;
  }[]
): Promise<(number | undefined)[]> {
  const ids: (number | undefined)[] = [];

  await asyncForEach(attachments, async (attachment) => {
    const { type, currentId, formValue, setMediaState, media } = attachment;

    if (formValue && formValue.length > 0) {
      const media = await uploadManfrediteFile(formValue[0], type);

      ids.push(media.id);
      setMediaState(media);
    } else if (currentId && (!formValue || formValue.length <= 0) && !media) {
      await deleteFile(currentId);

      ids.push(undefined);
    } else {
      ids.push(currentId);
    }
  });

  return ids;
}

/**
 * Returns the Yup validation schema for the Profile (Manfredite) form
 *
 * @param t - translation function (from next-i18n) for validation messages
 * @returns the validation schema for Profile Form
 */
export function getProfileFormValidationSchema(
  t: TFunction
): yup.AnyObjectSchema {
  return yup.object().shape({
    name: yup.string().nullable(),
    surname: yup.string().nullable(),
    about: yup.string().nullable(),
    headline: yup.string().nullable(),
    whereDoYouLive: yup
      .object()
      .shape({
        displayName: yupMultilingual(),
        placeId: yup.string()
      })
      .nullable(),
    softSkills: yupOptionalListItemArray({
      limit: 3,
      limitMessage: t('messages.error.maxSoftSkills', { count: 3 })
    }),

    roles: yupOptionalListItemArray(),
    techs: yupOptionalListItemArray(),
    techsLike: yupOptionalListItemArray(),
    techsDislike: yupOptionalListItemArray(),
    bestPractices: yup.string().nullable(),
    languages: yupOptionalListItemArray(),
    facts: yup
      .array()
      .nullable()
      .of(
        yup.object().shape({
          title: yup.string(),
          description: yup.string()
        })
      ),
    experience: yupOptionalListItem(),

    linkedIn: yup
      .string()
      .nullable()
      .matches(URL_OR_EMPTY_REGEX, t('messages.error.invalidUrl')),

    cv: yup.mixed().nullable(),
    mac: yup.mixed().nullable(),
    avatar: yup.mixed().nullable(),

    idealWorkplace: yup.string().nullable(),
    range: yupOptionalListItem(),
    rangeComment: yup.string().nullable(),

    phoneNumber: yup
      .string()
      .nullable()
      .test(
        'is-possible-number',
        t('messages.error.invalidPhoneNumber'),
        (value) => (value ? isPossiblePhoneNumber(value) : true)
      ),
    subscribe: yup.boolean().nullable(),

    locationTypeRemote: yup.boolean().nullable(),

    locationTypePartiallyRemote: yup
      .boolean()
      .nullable()
      .test({
        name: 'max',
        exclusive: false,
        message: t('messages.error.invalidLocation'),
        test: function (value) {
          const { workPlaces, locationAnywhere } = this.parent;

          if (
            value &&
            (!workPlaces || workPlaces.length <= 0) &&
            !locationAnywhere
          ) {
            return false;
          }

          return true;
        }
      }),
    locationTypeFaceToFace: yup
      .boolean()
      .nullable()
      .test({
        name: 'max',
        exclusive: false,
        message: t('messages.error.invalidLocation'),
        test: function (value) {
          const { workPlaces, locationAnywhere } = this.parent;

          if (
            value &&
            (!workPlaces || workPlaces.length <= 0) &&
            !locationAnywhere
          ) {
            return false;
          }

          return true;
        }
      }),

    locationAnywhere: yup.bool().nullable(),
    workPlaces: yup
      .array(
        yup.object().shape({
          displayName: yupMultilingual(),
          placeId: yup.string()
        })
      )
      .nullable(),

    workPlacesComment: yup.string().nullable(),

    socialLinks: yup.object().shape({
      twitter: yup
        .string()
        .nullable()
        .matches(
          TWITTER_URL_OR_EMPTY_REGEX,
          t('messages.error.invalidUrlTwitter')
        ),
      stackoverflow: yup
        .string()
        .nullable()
        .matches(
          SO_URL_OR_EMPTY_REGEX,
          t('messages.error.invalidUrlStackOverflow')
        ),
      github: yup
        .string()
        .nullable()
        .matches(
          GITHUB_URL_OR_EMPTY_REGEX,
          t('messages.error.invalidUrlGitHub')
        ),

      others: yup
        .array()
        .nullable()
        .of(
          yup.object().shape({
            url: yup
              .string()
              .nullable()
              .matches(URL_OR_EMPTY_REGEX, t('messages.error.invalidUrl')),
            desc: yup.string().nullable()
          })
        )
    })
  });
}

/**
 * @deprecated Use `mergeTechsListItemWithNotActiveTechs`
 */
export function getAvailableTechsListItems(
  techsSelected: Tech[] | undefined,
  items: ListItem[]
): ListItem[] {
  if (!techsSelected) {
    return [];
  }

  const disabledTechs: ListItem[] = (
    techsSelected?.filter((tech) => tech.enabled === false) || []
  ).map((l) => ({
    label: l.name,
    value: l.id
  }));

  return [...disabledTechs, ...(items || [])];
}

export function mergeTechsListItemWithNotActiveTechs(
  enabledTechsListItems: ListItem[],
  techs: Tech[]
): ListItem[] {
  return getAvailableTechsListItems(techs || [], enabledTechsListItems);
}

export type ManfrediteLevelInfo = {
  level: MANFREDITE_LEVEL;
  notFulfilledFieldsForPadawan: string[];
  allFieldsForPadawan: string[];
  notFulfilledFieldsForJedi: string[];
  allFieldsForJedi: string[];
};
